import { del, get, patch, post, type RequestOptions } from '@/api/client';
import {
  adaptAppointmentDetailsResponse,
  adaptAppointmentNotesResponse,
  type AppointmentDetailsFallback,
} from '../adapters/appointmentAdapters';
import type {
  AppointmentDetailsRaw,
  AppointmentDetailWire,
  AppointmentNote,
  AppointmentNotesResponseRaw,
  AppointmentOperatoryChangeResponse,
  AppointmentStatusAction,
  AppointmentStatusChangeResponse,
  EligibilityCheckResponse,
  KioskCodeResendResponse,
} from './types';

export const appointmentsApi = {
  getDetail: (id: number, options?: RequestOptions) =>
    get<AppointmentDetailWire>(`/api/appointments/${id}/detail`, undefined, options),

  changeStatus: (id: number, status: AppointmentStatusAction) =>
    patch<AppointmentStatusChangeResponse>(`/api/appointments/${id}/status`, { status }),

  changeOperatory: (id: number, operatoryId: string) =>
    patch<AppointmentOperatoryChangeResponse>(`/api/appointments/${id}/operatory`, {
      operatoryId,
    }),

  resendKioskCode: (id: number, channel?: 'sms' | 'email') =>
    post<KioskCodeResendResponse>(
      `/api/appointments/${id}/kiosk-code/resend`,
      channel ? { channel } : undefined,
    ),

  checkEligibility: (id: number) =>
    post<EligibilityCheckResponse>(`/api/appointments/${id}/eligibility/check`),

  getDetails: async (
    id: number,
    fallback?: AppointmentDetailsFallback,
    options?: RequestOptions,
  ) => {
    const raw = await get<AppointmentDetailsRaw>(`/api/appointments/${id}/details`, undefined, options);
    return adaptAppointmentDetailsResponse(raw, { appointmentId: id, ...fallback });
  },

  getNotes: async (id: number, includeArchived = false, options?: RequestOptions) => {
    const raw = await get<AppointmentNotesResponseRaw | AppointmentNote[]>(
      `/api/appointments/${id}/notes`,
      {
        ...(includeArchived ? { include_archived: true } : {}),
      },
      options,
    );
    return adaptAppointmentNotesResponse(raw);
  },

  createNote: (id: number, body: { note_text: string; visibility?: string }) =>
    post<AppointmentNote>(`/api/appointments/${id}/notes`, body),

  updateNote: (
    id: number,
    noteId: number,
    body: { note_text?: string; visibility?: string },
  ) => patch<AppointmentNote>(`/api/appointments/${id}/notes/${noteId}`, body),

  deleteNote: (id: number, noteId: number) =>
    del<void>(`/api/appointments/${id}/notes/${noteId}`),
};
